import type { Place, SearchAddressResult } from '@types';
import { IconType, ThemeType } from '@types';
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { getGeoService } from 'services/geoService';

import { AppContext } from '../../context/app';
import ButtonStyled from '../shared/button/Button';
import Icon from '../shared/icon/Icon';
import { Modal } from '../shared/modal/Modal';
import styles from './changeStartingLocationModal.module.scss';

type ChangeStartingLocationModalProps = {
  selectedStartingLocation: Place;
  // eslint-disable-next-line no-undef
  onSubmit: (place?: SearchAddressResult) => void;
  isOpen: boolean;
  onClose: () => void;
};

const ChangeStartingLocationModal: React.FC<
  ChangeStartingLocationModalProps
> = ({ isOpen, onSubmit, onClose, selectedStartingLocation }) => {
  const { t } = useTranslation('common');
  const { availableSites, setMapLoading } = useContext(AppContext);
  const [suggestedStartingLocation, setSuggestedStartingLocation] =
    // eslint-disable-next-line no-undef
    useState<SearchAddressResult | undefined>();

  const geoService = useMemo(() => getGeoService(), []);

  const formattedAddress = useMemo(() => {
    const startingBuildingAddress = availableSites
      ?.find((site) => site.id === selectedStartingLocation.location.site)
      ?.buildings?.find(
        (building) =>
          building.id === selectedStartingLocation.location.building,
      )?.address;

    return [
      startingBuildingAddress?.street,
      startingBuildingAddress?.city,
      startingBuildingAddress?.state,
      startingBuildingAddress?.zipcode,
    ].join(', ');
  }, [availableSites, selectedStartingLocation]);

  const fetchAddress = useCallback(
    async (address: string) => {
      const result = await geoService?.geocode(address);
      if (result) {
        setSuggestedStartingLocation(result);
      }
    },
    [geoService],
  );

  useEffect(() => {
    if (!suggestedStartingLocation) {
      setMapLoading(true);
      fetchAddress(formattedAddress);
    }
  }, [
    fetchAddress,
    formattedAddress,
    setMapLoading,
    suggestedStartingLocation,
  ]);

  return (
    <Modal isOpen={isOpen}>
      <div className={styles.container}>
        <h2 className={styles.title}>
          {t('campusNavigation.sameCampus.title')}
        </h2>
        <p className={styles.description}>
          {t('campusNavigation.sameCampus.description')}
        </p>
        <div className={styles.contentContainer}>
          <div className={styles.locationContainer}>
            <span className={styles.label}>
              {t('campusNavigation.sameCampus.selectedStartingLocationLabel')}
            </span>
            <div className={styles.locationNameContainer}>
              <Icon icon={IconType.LOCATION} theme={ThemeType.PRIMARY} />
              <span className={styles.startingLocationName}>
                {/*TODO: Maybe pass starting place as a prop rather than pulling from context? */}
                {selectedStartingLocation.name}
              </span>
            </div>
          </div>
          <div className={styles.locationContainer}>
            <span className={styles.label}>
              {t('campusNavigation.sameCampus.suggestedStartingLocationLabel')}
            </span>
            <div className={styles.locationNameContainer}>
              <Icon icon={IconType.LOCATION} theme={ThemeType.PRIMARY} />
              <span className={styles.suggestedLocationName}>
                {suggestedStartingLocation?.address}
              </span>
            </div>
          </div>
          <div className={styles.buttonContainer}>
            <ButtonStyled
              onClick={() => onSubmit(suggestedStartingLocation)}
              className={styles.button}
              uppercase
            >
              {t('campusNavigation.sameCampus.primaryButton')}
            </ButtonStyled>
            <ButtonStyled onClick={onClose} className={styles.button} uppercase>
              {t('campusNavigation.sameCampus.cancelButton')}
            </ButtonStyled>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default memo(ChangeStartingLocationModal);
